const HOST_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/

export type NormalizeResult =
  | { ok: true; host: string }
  | { ok: false; reason: 'empty' | 'invalid' | 'ip-address' | 'no-tld' }

/**
 * Turns whatever a user typed into the canonical host we store.
 *
 *   "https://WWW.Example.com/blog?a=1" -> "example.com"
 *   "münchen.de"                       -> "xn--mnchen-3ya.de"
 *
 * Leading "www." is dropped so a tenant cannot add the same site twice, and
 * the punycode form is stored so lookups against Search Console line up.
 */
export function normalizeHost(input: string): NormalizeResult {
  const trimmed = input.trim()
  if (!trimmed) return { ok: false, reason: 'empty' }

  let hostname: string
  try {
    const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    // The URL parser handles punycode, ports, credentials, paths and casing.
    hostname = new URL(withScheme).hostname.toLowerCase()
  } catch {
    return { ok: false, reason: 'invalid' }
  }

  // IPv6 arrives bracketed; IPv4 is four numeric labels. Neither is a website
  // Search Console will ever report on.
  if (hostname.startsWith('[') || /^\d+(\.\d+){3}$/.test(hostname)) {
    return { ok: false, reason: 'ip-address' }
  }

  const host = hostname.replace(/^www\./, '').replace(/\.$/, '')
  if (!host.includes('.')) return { ok: false, reason: 'no-tld' }
  if (host.length > 253 || !HOST_RE.test(host)) return { ok: false, reason: 'invalid' }

  return { ok: true, host }
}

export function normalizeHostOrThrow(input: string): string {
  const result = normalizeHost(input)
  if (!result.ok) throw new Error(`Invalid domain (${result.reason}): ${input}`)
  return result.host
}

/** Human-readable default for a newly added site. */
export function defaultDisplayName(host: string): string {
  return host
}

/**
 * The two Search Console properties that could plausibly back a host. Shown as
 * suggestions when the user links a site, rather than guessed silently.
 */
export function candidateGscProperties(host: string) {
  return [
    { type: 'sc-domain' as const, uri: `sc-domain:${host}` },
    { type: 'url-prefix' as const, uri: `https://${host}/` },
    { type: 'url-prefix' as const, uri: `https://www.${host}/` },
  ]
}
